import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Select, Typography, Layout, Spin, message, Row, Col, Button } from 'antd';
import KioskSimulator from '../components/simulator/KioskSimulator';
import { getAllStores, getPageVersionById, getSimulatorMenu } from '../services/simulatorservice';

const { Header, Content } = Layout;
const { Title } = Typography;
const { Option } = Select;

const SimulatorPreview: React.FC = () => {
    const { pageversionguid } = useParams<{ pageversionguid: string }>();

    const [storeNumber, setStoreNumber] = useState<string>('');
    const [pageVersionName, setPageVersionName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [storeOptions, setStoreOptions] = useState<string[]>([]);
    const [storeLoading, setStoreLoading] = useState<boolean>(true);
    const [menuData, setMenuData] = useState<any[] | null>(null);
    const [menuLoading, setMenuLoading] = useState<boolean>(false);
    const [scale, setScale] = useState<number>(0.45);
    const containerRef = useRef<HTMLDivElement>(null);

    // Fetch page version name
    useEffect(() => {
        const fetchPageVersionName = async () => {
            try {
                const data = await getPageVersionById(pageversionguid);
                setPageVersionName(data.name);
                
            } catch (error) {
                message.error('Failed to fetch page version name');
            } finally {
                setLoading(false);
            }
        };
        if (pageversionguid) fetchPageVersionName();
    }, [pageversionguid]);

    // Fetch stores
    useEffect(() => {
        const fetchStores = async () => {
            try {
                const data = await getAllStores();
                const stores = data.map((store: any) => store.storeNumber);
                setStoreOptions(stores);
            } catch (error) {
                message.error('Failed to fetch store list');
            } finally {
                setStoreLoading(false);
            }
        };
        fetchStores();
    }, []);

    // Fetch kiosk menu for this specific page version + store (store drives pricing)
    useEffect(() => {
        if (!pageversionguid || !storeNumber) return;
        const fetchMenu = async () => {
            setMenuLoading(true);
            setMenuData(null);
            try {
                const data = await getSimulatorMenu({
                    storeNumber,
                    pageVersionGuid: pageversionguid!,
                  });
                  setMenuData(data);
            } catch (error) {
                message.error('Failed to fetch menu data');
            } finally {
                setMenuLoading(false);
            }
        };
        fetchMenu();
    }, [pageversionguid, storeNumber]);

    // Auto-scale the 1080×1920 kiosk container to fit the viewport
    const updateScale = useCallback(() => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const availH = rect.height - 20;
        const availW = rect.width - 40;
        const scaleH = availH / 1920;
        const scaleW = availW / 1080;
        setScale(Math.min(scaleH, scaleW, 1));
    }, []);

    useEffect(() => {
        updateScale();
        window.addEventListener('resize', updateScale);
        return () => window.removeEventListener('resize', updateScale);
    }, [updateScale]);

    const handleStoreChange = (value: string) => {
        setStoreNumber(value);
    };

    return (
        <Layout style={{ height: '100vh' }}>
            <Header style={{ background: '#fff', padding: '8px 16px', borderBottom: '1px solid #eee' }}>
                <Row justify="start" align="middle">
                    <Col>
                        <Row align="middle" gutter={16}>
                            <Col>
                                <Title level={3} style={{ margin: 0 }}>Kiosk Simulator</Title>
                            </Col>
                            <Col>
                                {pageversionguid && (
                                    <Link to={`/kioskpageversion/${pageversionguid}`}>
                                        <Button color="primary" variant="filled">
                                            Go to Designer
                                        </Button>
                                    </Link>
                                )}
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        <Row align="middle" gutter={16}>
                            <Col>
                                {loading ? (
                                    <Spin />
                                ) : (
                                    <Title level={5} type="secondary" style={{ margin: 15 }}>
                                        {pageVersionName}
                                    </Title>
                                )}
                            </Col>
                            <Col>
                                {storeLoading ? (
                                    <Spin />
                                ) : (
                                    <Select
                                        showSearch
                                        placeholder="Choose Store"
                                        style={{ width: 150 }}
                                        onChange={handleStoreChange}
                                        value={storeNumber || undefined}
                                        optionFilterProp="children"
                                        allowClear
                                        filterOption={(input: string, option) => {
                                            const label = (option?.children ?? '') as string;
                                            return label.toLowerCase().includes(input.toLowerCase());
                                        }}
                                    >
                                        {storeOptions.map((store) => (
                                            <Option key={store} value={store}>{store}</Option>
                                        ))}
                                    </Select>
                                )}
                            </Col>
                        </Row>
                    </Col>                  
                </Row>
            </Header>

            <Content
                ref={containerRef}
                style={{ textAlign: 'center', overflow: 'hidden', background: '#1a1a2e', position: 'relative' }}
            >
                {!storeNumber && (
                    <div style={{ paddingTop: 80 }}>
                        <Title level={4} style={{ color: '#fff' }}>
                            Select a store to preview the kiosk
                        </Title>
                    </div>
                )}

                {storeNumber && menuLoading && (
                    <div style={{ paddingTop: 80 }}>
                        <Spin size="large" />
                        <div style={{ color: '#fff', marginTop: 16, fontWeight: 600 }}>
                            Loading menu for store {storeNumber}...
                        </div>
                    </div>
                )}

                {storeNumber && menuData && !menuLoading && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            height: '100%',
                            paddingTop: 10,
                        }}
                    >
                        <div
                            style={{
                                width: 1080,
                                height: 1920,
                                transformOrigin: 'top center',
                                transform: `scale(${scale})`,
                                position: 'relative',
                            }}
                        >
                            <KioskSimulator menuData={menuData} />
                        </div>
                    </div>
                )}
            </Content>
        </Layout>
    );
};

export default SimulatorPreview;
